import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Modal } from '@mui/material';

import asyncService from '../../../../../datahub/asyncService';
import ModalBody from '../../../../components/BatchActionModal/components/ModalBody';
import selectUser from '../../../../store/selectors/appSelector';
import { BULK_ACTION_TYPES } from '../../timekeepingConstants';
import BatchModalBody from '../BatchModalBody';

import {
  getApiRoute,
  getIdsOfTimecardsFromReadApiDocuments,
  getPrimaryButtonText,
} from './sharedHelpers';

const BatchProcessModalById = ({ isOpen, onClose, onGoBack, initialTotalHourCount }) => {
  const dispatch = useDispatch();

  const { token, refreshToken } = useSelector(selectUser);

  const [isProcessing, setIsProcessing] = useState(false);
  const [isStartedProcessing, setIsStartedProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [previousProcessedCount, setPreviousProcessedCount] = useState(null);
  const [isStuckAndForceAbort, setIsStuckAndForceAbort] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [actionError, setActionError] = useState(null); // TODO: This could be removed if we clean up the modal body subfile.

  const [completedIds, setCompletedIds] = useState([]);
  const [failedCount, setFailedCount] = useState(0);
  const [succeededCount, setSucceededCount] = useState(0);

  const initialTotalTimecardCount = isOpen?.placementIds?.length ? isOpen?.placementIds?.length : 0;

  const requestApi = useCallback(() => {
    const idsToGet = isOpen.placementIds.filter(
      (placementId) => !completedIds.includes(placementId)
    );
    asyncService({
      httpMethod: 'POST',
      route: getApiRoute(isOpen.modalType),
      data: {
        filters: [
          {
            field: '_id',
            operation: 'idContainedIn',
            value: idsToGet,
          },
        ],
      },
      onSuccess: (results) => {
        const newFailedIds = getIdsOfTimecardsFromReadApiDocuments(
          results?.data?.failed?.documents
        );
        const newSuccessIds = getIdsOfTimecardsFromReadApiDocuments(
          results?.data?.updated?.documents
        );

        const newFailedCount = newFailedIds?.length ? newFailedIds?.length : 0;
        const newSuccessCount = newSuccessIds?.length ? newSuccessIds?.length : 0;

        const currentProcessedCount = newFailedCount + newSuccessCount;

        setSucceededCount(succeededCount + newSuccessCount);
        setFailedCount(failedCount + newFailedCount);
        setCompletedIds([...completedIds, ...newSuccessIds, ...newFailedIds]);

        // We will detect cases where we get stuck by seeing if we are not processing anything two times in a row.
        // This can happen in happy path if there are multiple users trying to perform this operation at the same time.
        if (currentProcessedCount === 0 && currentProcessedCount === previousProcessedCount) {
          setIsStuckAndForceAbort(true);
          setIsComplete(true);
        }
        setPreviousProcessedCount(currentProcessedCount);

        setIsProcessing(false);
      },
      onError: () => {
        setIsProcessing(false);
        setIsComplete(true);
        onClose();
      },
      dispatch,
      token,
      refreshToken,
    });
  }, [
    dispatch,
    failedCount,
    completedIds,
    isOpen,
    onClose,
    previousProcessedCount,
    refreshToken,
    succeededCount,
    token,
  ]);

  useEffect(() => {
    const handleBatchApiRequest = () => {
      // Go and get more items.
      setIsProcessing(true);
      requestApi();
    };

    if (failedCount + succeededCount >= initialTotalTimecardCount) {
      setIsComplete(true);
    }

    if (
      isStartedProcessing && // We can start processing
      !isProcessing && // We are not actively processing
      failedCount + succeededCount < initialTotalTimecardCount && // We didn't finish yet
      !isStuckAndForceAbort // We are not stuck
    ) {
      handleBatchApiRequest();
    }
  }, [
    failedCount,
    succeededCount,
    isProcessing,
    isStartedProcessing,
    isStuckAndForceAbort,
    requestApi,
    initialTotalTimecardCount,
  ]);

  // Handle case where modal is not open
  if (!isOpen) {
    return null;
  }
  // Handle case where we have a non-supported bulk action type
  if (
    isOpen.modalType !== BULK_ACTION_TYPES.RESOLVE &&
    isOpen.modalType !== BULK_ACTION_TYPES.APPROVE &&
    isOpen.modalType !== BULK_ACTION_TYPES.FINALIZE
  ) {
    // TODO: add DD log error
    return `Bulk action type "${isOpen}" is not supported`;
  }

  return (
    <Modal
      open={!!isOpen}
      onClose={() => {
        if (isProcessing) {
          return;
        }
        setIsProcessing(false);
        onClose();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <ModalBody
        // smallScreen={false} // TODO: check for this value.
        headerKey={getPrimaryButtonText(isOpen.modalType)}
        BodyElement={() => (
          <BatchModalBody
            totalTimecardCount={initialTotalTimecardCount}
            totalHoursWorked={initialTotalHourCount}
          />
        )}
        actionError={actionError}
        hasStarted={isProcessing}
        isComplete={isComplete}
        isProcessing={isProcessing}
        failedCount={failedCount}
        updatedCount={succeededCount}
        totalItems={initialTotalTimecardCount}
        cancelButtonKey="buttonText.goBack"
        onClose={onClose}
        onGoBack={onGoBack}
        primaryButtonKey={getPrimaryButtonText(isOpen.modalType)}
        handleConfirm={() => {
          // Begin processing.
          setIsStartedProcessing(true);
        }}
      />
    </Modal>
  );
};

BatchProcessModalById.propTypes = {
  initialTotalHourCount: PropTypes.number.isRequired,
  isOpen: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onGoBack: PropTypes.func.isRequired,
};

export default BatchProcessModalById;
